import React from "react";
import { useTranslationStore } from "../../../state/useTranslationStore";
import AddLanguageButton from "./AddLanguageButton";
import { toast } from "sonner";

// Map known labels to shortcode tags for Vertical Daily Prayer
const VDP_SHORTCODES: Record<string, string> = {
  "Current Local Time": "[Fp_Single_CurrentTime]",
  "Current Date": "[Fp_Single_CurrentDate]",
  "Current Prayer": "[Fp_Single_Current_Title]",
  "Prayer": "[Fp_Single_Prayer_Title]",
  "Next Prayer": "[Fp_Single_Next_Prayer_Title]",
  Begins: "[Fp_Single_Prayer_Begins_Title]",
  Iqamah: "[Fp_Single_Prayer_Iqamah_Title]",
  // fallback prayer names if present here
  Fajr: "[Fp_Single_Fajr_Name]",
  Sunrise: "[Fp_Single_Sunrise_Name]",
  Dhuhr: "[Fp_Single_Dhuhr_Name]",
  Asr: "[Fp_Single_Asr_Name]",
  Maghrib: "[Fp_Single_Maghrib_Name]",
  Isha: "[Fp_Single_Isha_Name]",
};
// Single fixed badge class for consistency
const badgeClass =
  "ml-2 inline-block px-2 py-0.5 text-xs rounded border bg-green-100 text-green-800 border-green-200";

const Vertical_Daily_Prayer: React.FC = () => {
  const {
    vertical_daily_prayer,
    activeLanguage,
    availableLanguages,
    loading,
    selectLanguage,
    updateTranslation,
    saveTranslations,
    addNewLanguage,
  } = useTranslationStore();

  // Filter vertical_daily_prayer for the active language and track original index
  const activeVerticalDailyPrayer = vertical_daily_prayer
    .map((item, idx) => ({ ...item, originalIndex: idx }))
    .filter((item) => item.language_code === activeLanguage);

  // Handle input changes using original index
  const handleInputChange = (originalIndex: number, value: string) => {
    if (originalIndex >= 0 && originalIndex < vertical_daily_prayer.length) {
      updateTranslation("vertical_daily_prayer", originalIndex, value);
    } else {
      console.error("Invalid index for vertical_daily_prayer");
    }
  };

  // Handle saving translations
  const handleSave = async () => {
    try {
      await saveTranslations();
      toast.success("Vertical daily prayer updated successfully!");
    } catch (error) {
      console.error("Error saving vertical daily prayer:", error);
      toast.error("Failed to save vertical daily prayer.");
    }
  };

  return (
    <div className="vertical-daily-prayer-editor p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-green-600">
        Edit Vertical Daily Prayer
      </h1>

      {/* Language Selection */}
      <div className="mb-4 flex gap-4 flex-wrap">
        <div className="space-x-3">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={`px-3 py-2 rounded-md focus:outline-none focus:ring-2 capitalize ${
                activeLanguage === lang.code
                  ? "bg-green-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {lang.language}
            </button>
          ))}
        </div>
        <AddLanguageButton
          data={vertical_daily_prayer}
          handlerAddLangauge={addNewLanguage}
          disabled={loading}
        />
      </div>

      {/* Translation Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeVerticalDailyPrayer.map((item) => (
          <div
            key={`${item.original_text}-${item.language_code}`}
            className="flex flex-col gap-2"
          >
            <label className="text-gray-700 font-medium">
              {item.original_text} ({item.language_code})
              {VDP_SHORTCODES[item.original_text] && (
                <span
                  className={badgeClass}
                >
                  {VDP_SHORTCODES[item.original_text]}
                </span>
              )}
            </label>
            <input
              type="text"
              value={item.translated_text}
              onChange={(e) => handleInputChange(item.originalIndex, e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default Vertical_Daily_Prayer;
